<template>
  <section
    class="my-8 md:mb-8 mx-auto bg-black pt-6 px-2 md:px-10 pb-0 shadow-black shadow-xl divide-y-2 divide-white font-helvetica text-[22px] text-[#999999] max-w-[1400px]"
  >
    <RallyLogoDownload :to="downloadTo" />

    <!-- Language switcher -->
    <div>
      <LanguageNav :current="$route.meta.lang" />
    </div>

    <!-- Hero -->
    <div class="pt-8 flex flex-col gap-4">
      <h1 class="text-white text-2xl md:text-4xl font-russo">{{ c.heroH1 }}</h1>
      <h2 class="font-semibold text-white">{{ c.heroH2 }}</h2>
      <p class="text-[17px] md:text-[20px]" v-html="c.heroIntro"></p>
      <RoadbookSamplePair
        :cc-alt="discName('/how-to-create-a-cross-country')"
        :rr-alt="discName('/how-to-create-a-road-rally')"
      />
    </div>

    <RoadbookCtas
      :download-label="c.downloadLinkText"
      :download-to="downloadTo"
      :pro-label="c.proFeaturesText"
      :aria-label="`${c.downloadLinkText} · ${c.proFeaturesText}`"
    />

    <!-- Software available in your language -->
    <LanguageSupport
      v-if="c.langSupport"
      :heading="c.langSupport.heading"
      :body="c.langSupport.body"
      :img="c.langSupport.img"
      :width="c.langSupport.w"
      :height="c.langSupport.h"
      :alt="c.langSupport.alt"
      :map-langs="c.langSupport.mapLangs"
    />

    <!-- Software UI language list (for locales the app UI doesn't cover) -->
    <LanguageAvailability
      v-if="c.langAvailable"
      :heading="c.langAvailable.heading"
      :body="c.langAvailable.body"
      :menu-path="c.langAvailable.menuPath"
      :map-langs="c.langAvailable.mapLangs"
    />

    <!-- What is a roadbook -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">{{ c.whatH2 }}</h2>
      <p class="text-[17px] md:text-[20px]" v-html="c.whatP1"></p>
      <p class="text-[17px] md:text-[20px]" v-html="c.whatP2"></p>
      <picture class="contents">
        <source
          srcset="/images/create-road-stage/RR-Screenshot-1.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="1919"
          height="1029"
          src="/images/create-road-stage/RR-Screenshot-1.png"
          class="flex m-auto"
          :alt="c.heroH1"
        />
      </picture>
    </div>

    <!-- One editor for every discipline -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">{{ c.discH2 }}</h2>
      <p class="text-[17px] md:text-[20px]">{{ c.discIntro }}</p>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-3 font-arial text-[16px] md:text-[18px] font-bold"
      >
        <li v-for="(item, i) in c.disc" :key="i" v-html="item"></li>
      </ul>
    </div>

    <!-- How to create -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">{{ c.howH2 }}</h2>
      <picture class="contents">
        <source srcset="/images/download/toolbar3-1.webp" type="image/webp" />
        <img
          decoding="async"
          loading="lazy"
          width="1920"
          height="1080"
          src="/images/download/toolbar3-1.png"
          class="flex m-auto"
          :alt="c.howH2"
        />
      </picture>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-3 font-arial text-[16px] md:text-[18px] font-bold"
      >
        <li v-for="(item, i) in c.how" :key="i">{{ item }}</li>
      </ul>
    </div>

    <!-- Print & export -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo" v-html="c.printH2"></h2>
      <p class="text-[17px] md:text-[20px]">{{ c.printP }}</p>
      <picture class="contents">
        <source
          srcset="/images/OpenRallyGPX-Screen-Shot.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="1200"
          height="700"
          src="/images/OpenRallyGPX-Screen-Shot.png"
          class="flex m-auto"
          alt="OpenRally GPX"
        />
      </picture>
    </div>

    <!-- Digital roadbook -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">
        {{ c.digitalH2 }}
      </h2>
      <p class="text-[17px] md:text-[20px]" v-html="c.digitalP"></p>
      <a :href="downloadTo" class="m-auto">
        <h2
          class="text-[#FF0000] font-russo text-[22px] md:text-[28px] hover:underline text-center"
        >
          {{ c.ctaText }}
        </h2>
      </a>
    </div>
  </section>

  <section class="my-8 mx-auto max-w-[1400px]">
    <FeaturesBanner />
    <PartnersSmall />
    <RallyLogoDownload :link-text="c.downloadLinkText" :to="downloadTo" />
  </section>
</template>

<script>
import FeaturesBanner from './FeaturesBanner.vue';
import PartnersSmall from './PartnersSmall.vue';
import RallyLogoDownload from './RallyLogoDownload.vue';
import LanguageSupport from './LanguageSupport.vue';
import LanguageAvailability from './LanguageAvailability.vue';
import LanguageNav from './LanguageNav.vue';
import RoadbookCtas from './RoadbookCtas.vue';
import RoadbookSamplePair from './RoadbookSamplePair.vue';
import { downloadPath } from '../content/downloadPage';
import { ROADBOOK_CONTENT } from '../content/rallyRoadbook';

export default {
  name: 'RallyRoadbookI18n',
  components: {
    RoadbookCtas,
    RoadbookSamplePair,
    FeaturesBanner,
    PartnersSmall,
    RallyLogoDownload,
    LanguageSupport,
    LanguageAvailability,
    LanguageNav,
  },
  computed: {
    c() {
      return ROADBOOK_CONTENT[this.$route.meta.lang] || ROADBOOK_CONTENT.de;
    },
    // fr/es/it have their own download page; other languages use the English one.
    downloadTo() {
      return downloadPath(this.$route.meta.lang);
    },
  },
  methods: {
    /** Translated discipline name (the link text in c.disc) for an image alt. */
    discName(href) {
      const item = this.c.disc.find((d) => d.includes(href));
      const m = item && item.match(/<a[^>]*>(.*?)<\/a>/);
      return m ? m[1].replace(/&amp;/g, '&') : this.c.heroH1;
    },
  },
};
</script>

<style scoped></style>
